/**
 * The painter's residual layout, as a calibrated part of the painter projection.
 *
 * The pinhole camera plus forward compression leaves the painted landmarks 68 px (inlier RMS, 2000
 * px frame) from where they render, and the misfit is mostly horizontal and spatially coherent
 * (STYLE §2). tools/painterWarp fits a thin-plate spline to it: a shift along each painting-frame
 * row as a function of where the point renders, stored in painterView.json `residualWarp`. Where
 * the vertical residuals predict each other too (leave-one-out), it adds a shift down each column
 * that vanishes on the painted horizon row, so the horizon stays where the painter drew it, and
 * below the lowest fitted point keeps the shift it has on that point's row; where they do not, rows
 * stay put.
 *
 * The shift is applied in world space, along the painter camera's right and up axes at constant
 * painter depth, so the painter camera sees each point exactly the fitted number of pixels further
 * along its row and down its column, and every other camera sees one smooth deformation that fades
 * with the painter amount. painterProjection.glsl mirrors painterFrameOffset, row and column;
 * warpAnchor.ts moves objects that must keep their drawn shape by the field at one anchor point.
 */

import type { CameraPose } from '../data/types';
import type { Vec2, Vec3 } from '../geo/types';
import type { PainterProjectionConstants } from './painterProjection';

/*
 * Painting-frame pixels run x right and y down, square, so a shift along a row is a move along the
 * painter camera's right axis.
 */
export interface PainterFrame {
  readonly eye: Vec3;
  readonly right: Vec3;
  readonly up: Vec3;
  readonly forward: Vec3;
  readonly focalPx: number;
  readonly principalPx: Vec2;
}

export interface FramePoint {
  readonly pixel: Vec2;
  readonly depth: number;
}

/**
 * A field in pixels of the frame: affine part plus radial terms r^2 ln r, with r the distance to
 * each centre in units of scalePx.
 */
export interface ThinPlateField {
  readonly scalePx: number;
  readonly centresPx: readonly Vec2[];
  readonly weights: readonly number[];
  readonly affine: readonly [number, number, number];
}

/**
 * Column shift Dy(u, v) = (w - heldRowPx) / scalePx h(u, w), w = min(v, lastFittedRowPx), with h a
 * field like the row shift: it vanishes on the painted horizon row, where the painter camera's
 * horizon parameters hold it, and below the lowest row it was fitted on it keeps, column by column,
 * the shift it has on that row, where extrapolating the spline would move the harbour band by
 * whatever the lowest points' bend grows to.
 */
export interface VerticalShiftField extends ThinPlateField {
  readonly heldRowPx: number;
  readonly lastFittedRowPx: number;
}

/** Horizontal shift field D(u, v) in pixels, and the column shift where the fit found one. */
export interface PainterResidualWarp extends ThinPlateField {
  readonly vertical?: VerticalShiftField;
}

/*
 * Newton stops once the unwarped pixel reproduces the painted one to this many pixels; with the
 * smallest singular value of the warp's Jacobian at least 0.59 over the canvas, the unwarped pixel
 * then lies within 2e-6 px of the exact inverse.
 */
export const UNWARP_TOLERANCE_PX = 1e-6;
const UNWARP_MAX_STEPS = 30;

/*
 * The painter frame's nearest ground is 2.66 km along the optical axis, so fading the shift in over
 * the first kilometre only touches geometry outside the frame and holds points behind the image
 * plane, where the pixel is undefined, in place.
 */
export const WARP_FADE_DEPTH_M = 1000;

/*
 * Pixels further than this from the principal point lie outside any canvas at the painter view,
 * even 15 degrees off it; clamping there keeps the float32 kernel sums of the GLSL mirror finite.
 */
export const WARP_LIMIT_PX = 10000;

function dot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function normalize(v: Vec3): Vec3 {
  const length = Math.sqrt(dot(v, v));
  return [v[0] / length, v[1] / length, v[2] / length];
}

function cross(a: Vec3, b: Vec3): Vec3 {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}

function clamp(x: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, x));
}

function smoothstep(e0: number, e1: number, x: number): number {
  const t = clamp((x - e0) / (e1 - e0), 0, 1);
  return t * t * (3 - 2 * t);
}

/*
 * The look-at basis of the painter pose, which is the basis of the app camera placed at that pose
 * (roll 0), so the frame projects exactly what the painter view renders.
 */
export function painterFrameFromView(
  view: CameraPose,
  constants: Pick<PainterProjectionConstants, 'imageWidth' | 'imageHeight'>
): PainterFrame {
  const forward = normalize([
    view.target[0] - view.position[0],
    view.target[1] - view.position[1],
    view.target[2] - view.position[2],
  ]);
  const right = normalize(cross(forward, [0, 1, 0]));
  const up = cross(right, forward);
  const halfFovY = (view.fovYDeg * Math.PI) / 360;
  return {
    eye: view.position,
    right,
    up,
    forward,
    focalPx: constants.imageHeight / 2 / Math.tan(halfFovY),
    principalPx: [constants.imageWidth / 2, constants.imageHeight / 2],
  };
}

export function projectToPainterFrame(frame: PainterFrame, position: Vec3): FramePoint {
  const rel: Vec3 = [
    position[0] - frame.eye[0],
    position[1] - frame.eye[1],
    position[2] - frame.eye[2],
  ];
  const depth = dot(rel, frame.forward);
  return {
    pixel: [
      frame.principalPx[0] + (frame.focalPx * dot(rel, frame.right)) / depth,
      frame.principalPx[1] - (frame.focalPx * dot(rel, frame.up)) / depth,
    ],
    depth,
  };
}

function fieldValue(field: ThinPlateField, pixel: Vec2): number {
  const u = pixel[0] / field.scalePx;
  const v = pixel[1] / field.scalePx;
  let value = field.affine[0] + field.affine[1] * u + field.affine[2] * v;
  for (let i = 0; i < field.centresPx.length; i++) {
    const centre = field.centresPx[i]!;
    const du = u - centre[0] / field.scalePx;
    const dv = v - centre[1] / field.scalePx;
    const r2 = du * du + dv * dv;
    if (r2 > 0) value += field.weights[i]! * 0.5 * r2 * Math.log(r2);
  }
  return value;
}

function fieldGradient(field: ThinPlateField, pixel: Vec2): Vec2 {
  const u = pixel[0] / field.scalePx;
  const v = pixel[1] / field.scalePx;
  let gu = field.affine[1];
  let gv = field.affine[2];
  for (let i = 0; i < field.centresPx.length; i++) {
    const centre = field.centresPx[i]!;
    const du = u - centre[0] / field.scalePx;
    const dv = v - centre[1] / field.scalePx;
    const r2 = du * du + dv * dv;
    if (r2 > 0) {
      const radial = field.weights[i]! * (Math.log(r2) + 1);
      gu += radial * du;
      gv += radial * dv;
    }
  }
  return [gu / field.scalePx, gv / field.scalePx];
}

export function residualShiftPx(warp: PainterResidualWarp, pixel: Vec2): number {
  return fieldValue(warp, pixel);
}

export function residualShiftGradient(warp: PainterResidualWarp, pixel: Vec2): Vec2 {
  return fieldGradient(warp, pixel);
}

/** Where the column shift is evaluated for a pixel: on the last fitted row for a pixel below it. */
export function columnShiftPixel(field: VerticalShiftField, pixel: Vec2): Vec2 {
  return pixel[1] > field.lastFittedRowPx ? [pixel[0], field.lastFittedRowPx] : pixel;
}

/** The column shift Dy at a rendered pixel, 0 for a warp without one. */
export function verticalShiftPx(warp: PainterResidualWarp, pixel: Vec2): number {
  const field = warp.vertical;
  if (!field) return 0;
  const at = columnShiftPixel(field, pixel);
  return ((at[1] - field.heldRowPx) / field.scalePx) * fieldValue(field, at);
}

/**
 * [dDy/du, dDy/dv], [0, 0] for a warp without a column shift. Below the last fitted row the shift
 * does not change down a column; on the row it is the spline's own slope.
 */
export function verticalShiftGradient(warp: PainterResidualWarp, pixel: Vec2): Vec2 {
  const field = warp.vertical;
  if (!field) return [0, 0];
  const at = columnShiftPixel(field, pixel);
  const lever = (at[1] - field.heldRowPx) / field.scalePx;
  const [hu, hv] = fieldGradient(field, at);
  if (pixel[1] > field.lastFittedRowPx) return [lever * hu, 0];
  return [lever * hu, fieldValue(field, at) / field.scalePx + lever * hv];
}

/** [D, Dy]: the shift along the row and down the column, pixels. */
export function residualShift(warp: PainterResidualWarp, pixel: Vec2): Vec2 {
  return [residualShiftPx(warp, pixel), verticalShiftPx(warp, pixel)];
}

/** The gradients of the row and the column shift: [[dD/du, dD/dv], [dDy/du, dDy/dv]]. */
export function residualShiftJacobian(
  warp: PainterResidualWarp,
  pixel: Vec2
): readonly [Vec2, Vec2] {
  return [residualShiftGradient(warp, pixel), verticalShiftGradient(warp, pixel)];
}

export function warpToPainted(warp: PainterResidualWarp, rendered: Vec2): Vec2 {
  return [
    rendered[0] + residualShiftPx(warp, rendered),
    rendered[1] + verticalShiftPx(warp, rendered),
  ];
}

/* One Newton step on r + D(r) = painted, whose Jacobian is the identity plus the shift's. */
function newtonStep(
  rendered: Vec2,
  error: Vec2,
  [[xu, xv], [yu, yv]]: readonly [Vec2, Vec2]
): Vec2 {
  const [a, b, c, d] = [1 + xu, xv, yu, 1 + yv];
  const determinant = a * d - b * c;
  return [
    rendered[0] - (d * error[0] - b * error[1]) / determinant,
    rendered[1] - (a * error[1] - c * error[0]) / determinant,
  ];
}

/**
 * The rendered pixel the painter drew at `painted`: Newton on r + D(r) = painted, which keeps to the
 * row where the warp has no column shift. The fit keeps the Jacobian determinant of the warp above
 * 0.5 over the canvas, so the map is one-to-one there and Newton converges from the first-order guess
 * in a few steps.
 */
export function unwarpFromPainted(warp: PainterResidualWarp, painted: Vec2): Vec2 {
  let rendered: Vec2 = [
    painted[0] - residualShiftPx(warp, painted),
    painted[1] - verticalShiftPx(warp, painted),
  ];
  for (let step = 0; step < UNWARP_MAX_STEPS; step++) {
    const warped = warpToPainted(warp, rendered);
    const error: Vec2 = [warped[0] - painted[0], warped[1] - painted[1]];
    if (Math.hypot(error[0], error[1]) < UNWARP_TOLERANCE_PX) return rendered;
    rendered = newtonStep(rendered, error, residualShiftJacobian(warp, rendered));
  }
  throw new Error(`Painter warp inverse did not converge at (${painted[0]}, ${painted[1]})`);
}

/*
 * The field at a projected point's painting-frame pixel, faded in over the first kilometre of
 * painter depth and scaled by the painter amount. The pixel is limited as in the shader.
 */
export function painterFrameShift(
  frame: PainterFrame,
  warp: PainterResidualWarp,
  position: Vec3,
  amount: number
): Vec2 {
  if (amount < 0.001) return [0, 0];
  const { pixel, depth } = projectToPainterFrame(frame, position);
  const fade = smoothstep(0, WARP_FADE_DEPTH_M, depth);
  if (fade <= 0) return [0, 0];
  const limited: Vec2 = [
    clamp(pixel[0], frame.principalPx[0] - WARP_LIMIT_PX, frame.principalPx[0] + WARP_LIMIT_PX),
    clamp(pixel[1], frame.principalPx[1] - WARP_LIMIT_PX, frame.principalPx[1] + WARP_LIMIT_PX),
  ];
  const [along, down] = residualShift(warp, limited);
  return [along * fade * amount, down * fade * amount];
}

/** The row part of painterFrameShift. */
export function painterFrameShiftPx(
  frame: PainterFrame,
  warp: PainterResidualWarp,
  position: Vec3,
  amount: number
): number {
  return painterFrameShift(frame, warp, position, amount)[0];
}

/* The move along the painter right axis, at constant painter depth, that the painter camera sees
   as shiftPx pixels along the point's row. */
export function rowShiftOffset(frame: PainterFrame, position: Vec3, shiftPx: number): Vec3 {
  if (shiftPx === 0) return [0, 0, 0];
  const rel: Vec3 = [
    position[0] - frame.eye[0],
    position[1] - frame.eye[1],
    position[2] - frame.eye[2],
  ];
  const metres = (shiftPx * dot(rel, frame.forward)) / frame.focalPx;
  return [frame.right[0] * metres, frame.right[1] * metres, frame.right[2] * metres];
}

/*
 * The move at constant painter depth that the painter camera sees as `shift` pixels: along the
 * point's row, and down its column against the camera's up axis, since rows count downwards.
 */
export function frameShiftOffset(frame: PainterFrame, position: Vec3, shift: Vec2): Vec3 {
  const along = rowShiftOffset(frame, position, shift[0]);
  if (shift[1] === 0) return along;
  const rel: Vec3 = [
    position[0] - frame.eye[0],
    position[1] - frame.eye[1],
    position[2] - frame.eye[2],
  ];
  const metres = (shift[1] * dot(rel, frame.forward)) / frame.focalPx;
  return [
    along[0] - frame.up[0] * metres,
    along[1] - frame.up[1] * metres,
    along[2] - frame.up[2] * metres,
  ];
}

/**
 * World offset that moves `position` (already through the forward compression), at constant
 * painter depth, by the residual shift times `amount`.
 */
export function painterFrameOffset(
  frame: PainterFrame,
  warp: PainterResidualWarp,
  position: Vec3,
  amount: number
): Vec3 {
  return frameShiftOffset(frame, position, painterFrameShift(frame, warp, position, amount));
}
